import { proposalService } from './proposalService';
import { conceptMapService } from './conceptMapService';
import { documentService } from './documentService';
import { roadmapService } from './roadmapService';
import { checklistService } from './checklistService';
import { documentChecklistService } from './documentChecklistService';
import { outputFormService } from './outputFormService';

function truncate(text: string, max: number): string {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '...' : text;
}

export interface RoadmapContextItem {
  id: number;
  topic: string;
  content?: string;
  instruction?: string;
  character_limit?: number | null;
  word_limit?: number | null;
  limit_type?: 'characters' | 'words' | null;
}

export interface ProposalAIContext {
  contextText: string;
  proposalTitle: string;
  roadmapItems: RoadmapContextItem[];
  conceptMap: Record<string, unknown>;
  checklistItems: Record<string, unknown>[];
  docChecklistItems: Record<string, unknown>[];
}

/**
 * Constrói o contexto completo de uma proposta para uso pelo Chat IA.
 * Único ponto de verdade — usado por ChatIAWidget e ChatTab.
 */
export async function buildProposalAIContext(proposalId: number): Promise<ProposalAIContext> {
  let proposalTitle = '';
  let roadmapItems: RoadmapContextItem[] = [];
  let conceptMap: Record<string, unknown> = {};
  let checklistItems: Record<string, unknown>[] = [];
  let docChecklistItems: Record<string, unknown>[] = [];

  // As 7 buscas abaixo são independentes entre si — disparadas em paralelo em vez
  // de sequencialmente, pra não somar a latência de cada uma na abertura do chat.
  const [
    proposalResult,
    conceptMapResult,
    documentsResult,
    roadmapResult,
    checklistResult,
    docChecklistResult,
    outputStatusResult,
  ] = await Promise.allSettled([
    proposalService.getById(proposalId),
    conceptMapService.getByProposal(proposalId),
    documentService.getContentByProposal(proposalId),
    roadmapService.getByProposal(proposalId),
    checklistService.getByProposal(proposalId),
    documentChecklistService.getByProposal(proposalId),
    outputFormService.getStatus(proposalId),
  ]);

  // A ordem das seções no texto final segue a ordem abaixo, independente da
  // ordem em que cada request terminou.
  const proposalSection: string[] = [];
  if (proposalResult.status === 'fulfilled' && proposalResult.value) {
    const proposal = proposalResult.value;
    proposalTitle = proposal.title;
    proposalSection.push(`### Proposta: "${proposal.title}" (Tipo: ${proposal.type})`);
    if (proposal.guidance_text) {
      proposalSection.push(`### Orientações do Edital\n${truncate(proposal.guidance_text, 1500)}`);
    }
  }

  const conceptMapSection: string[] = [];
  if (conceptMapResult.status === 'fulfilled' && conceptMapResult.value && Object.keys(conceptMapResult.value).length > 0) {
    const cm = conceptMapResult.value;
    conceptMap = cm as Record<string, unknown>;
    const cmLines: string[] = [];
    if (cm.project_idea) cmLines.push(`- **Ideia:** ${truncate(cm.project_idea, 400)}`);
    if (cm.publico_alvo) cmLines.push(`- **Público-Alvo:** ${truncate(cm.publico_alvo, 300)}`);
    if (cm.problema_central) cmLines.push(`- **Problema Central:** ${truncate(cm.problema_central, 300)}`);
    if (cm.solucao_proposta) cmLines.push(`- **Solução:** ${truncate(cm.solucao_proposta, 300)}`);
    const tomVoz = cm.tom_de_voz || cm.tom_voz;
    if (tomVoz) cmLines.push(`- **Tom de Voz:** ${truncate(tomVoz, 200)}`);
    const linguagem = cm.linguagem || cm.diretrizes_linguagem;
    if (linguagem) cmLines.push(`- **Linguagem:** ${truncate(linguagem, 200)}`);
    const objetivos: string[] = (cm.objetivos_principais as string[]) || [];
    if (objetivos.length) cmLines.push(`- **Objetivos:** ${objetivos.map(o => truncate(o, 100)).join('; ')}`);
    if (cmLines.length > 0) conceptMapSection.push(`### Mapa Conceitual\n${cmLines.join('\n')}`);
  }

  const documentsSection: string[] = [];
  if (documentsResult.status === 'fulfilled' && documentsResult.value && documentsResult.value.length > 0) {
    const docsContent = documentsResult.value;
    const docLines = docsContent.map((doc, i) => {
      const header = `${i + 1}. **${doc.title}**${doc.context ? ' — ' + truncate(doc.context, 150) : ''}`;
      if (doc.has_content && doc.text_content) {
        return `${header}\n${truncate(doc.text_content, 2000)}`;
      }
      return `${header} *(sem conteúdo extraído)*`;
    });
    documentsSection.push(`### Documentos (${docsContent.length})\n\n${docLines.join('\n\n---\n\n')}`);
  }

  const roadmapSection: string[] = [];
  if (roadmapResult.status === 'fulfilled' && roadmapResult.value && roadmapResult.value.length > 0) {
    const roadmap = roadmapResult.value;
    roadmapItems = roadmap.map(item => ({
      id: item.id,
      topic: item.topic,
      content: item.content || '',
      instruction: item.instruction || '',
      character_limit: item.character_limit ?? null,
      word_limit: item.word_limit ?? null,
      limit_type: item.limit_type ?? null,
    }));
    const lines = roadmap.map((item, i) => {
      const status = item.content?.trim() ? '✅' : '⬜';
      return `${i + 1}. ${status} **${item.topic}**`;
    });
    roadmapSection.push(`### Roteiro (${roadmap.length} tópicos)\n${lines.join('\n')}`);

    const elaborated = roadmap.filter(item => item.content?.trim());
    if (elaborated.length > 0) {
      // character_limit/word_limit não são garantia — nada no sistema impede edição
      // manual além do limite configurado. Por isso não confiamos nesses campos: usamos
      // o tamanho real do conteúdo, só protegendo contra um caso patológico com um teto
      // de segurança bem generoso (raramente atingido por conteúdo real).
      const CONTEXT_SECTION_CEILING = 20000;
      const contentLines = elaborated.map(item => `#### ${item.topic}\n${truncate(item.content || '', CONTEXT_SECTION_CEILING)}`);
      roadmapSection.push(`### Conteúdo Elaborado (${elaborated.length}/${roadmap.length})\n${contentLines.join('\n\n')}`);
    }
  }

  const checklistSection: string[] = [];
  if (checklistResult.status === 'fulfilled' && checklistResult.value && checklistResult.value.length > 0) {
    const checklist = checklistResult.value;
    checklistItems = checklist.map(i => ({
      id: i.id, text: i.text, item_group: i.item_group ?? null, evaluation_level: i.evaluation_level ?? null,
    }));
    const evaluated = checklist.filter(i => i.evaluation_level).length;
    checklistSection.push(`### Checklist de Enquadramento (${checklist.length} itens, ${evaluated} avaliados)`);
  }

  const docChecklistSection: string[] = [];
  if (docChecklistResult.status === 'fulfilled' && docChecklistResult.value && docChecklistResult.value.length > 0) {
    const docChecklist = docChecklistResult.value;
    docChecklistItems = docChecklist.map(i => ({
      id: i.id, text: i.text, item_group: i.item_group ?? null, evaluation_level: i.evaluation_level ?? null,
    }));
    const evaluated = docChecklist.filter(i => i.evaluation_level).length;
    docChecklistSection.push(`### Checklist Documental (${docChecklist.length} itens, ${evaluated} avaliados)`);
  }

  const outputStatusSection: string[] = [];
  if (outputStatusResult.status === 'fulfilled' && outputStatusResult.value?.sections?.length > 0) {
    const outputStatus = outputStatusResult.value;
    outputStatusSection.push(
      `### Qualidade da Proposta\n` +
      `- Score: ${outputStatus.quality_score}%\n` +
      `- Seções obrigatórias: ${outputStatus.required_complete}/${outputStatus.required_total} completas`
    );
  }

  const sections = [
    ...proposalSection,
    ...conceptMapSection,
    ...documentsSection,
    ...roadmapSection,
    ...checklistSection,
    ...docChecklistSection,
    ...outputStatusSection,
  ];

  return {
    contextText: sections.join('\n\n'),
    proposalTitle,
    roadmapItems,
    conceptMap,
    checklistItems,
    docChecklistItems,
  };
}
